import { customFetch } from '../../services/apiService';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Search, Filter, Download, Trash2, Edit3, Coins, Plus, Check, 
  UserCheck, Shield, ChevronDown, RefreshCw, X, Loader2, Sparkles, User,
  Crown, Gem, Calendar, Clock, AlertCircle, Info, CheckCircle2,
  Users
} from 'lucide-react';
import { UserProfile } from '../../types';
import { Card } from '../ui/Card';
import CustomDialog from '../CustomDialog';
import { VipBadge } from '../ui/VipBadge';
import { useDebounce } from '../../hooks/useDebounce';
import { ServerPagination } from '../ui/ServerPagination';
import { useAdminUsers } from '../../hooks/useQueries';
import { useQueryClient } from '@tanstack/react-query';

interface UserManagementProps {
  onRefresh?: () => void;
}

export const UserManagement: React.FC<UserManagementProps> = () => {
  const queryClient = useQueryClient();

  // Read initial URL params if available
  const getInitialParams = () => {
    if (typeof window === 'undefined') {
      return { page: 1, limit: 10, search: '', status: 'all', grade: 'all', province: 'all' };
    }
    const searchParams = new URLSearchParams(window.location.search);
    return {
      page: parseInt(searchParams.get('page') || '1', 10) || 1,
      limit: parseInt(searchParams.get('limit') || '10', 10) || 10,
      search: searchParams.get('search') || '',
      status: searchParams.get('status') || searchParams.get('role') || 'all',
      grade: searchParams.get('grade') || 'all',
      province: searchParams.get('province') || 'all'
    };
  };

  const initialParams = useMemo(() => getInitialParams(), []);

  // 1. Quản lý State cho API Pagination & Filters
  const [currentPage, setCurrentPage] = useState<number>(initialParams.page);
  const [pageSize, setPageSize] = useState<number>(initialParams.limit);
  const [searchQuery, setSearchQuery] = useState<string>(initialParams.search);
  const [filterStatus, setFilterStatus] = useState<string>(initialParams.status);
  const [selectedGrade, setSelectedGrade] = useState<string>(initialParams.grade);
  const [selectedProvince, setSelectedProvince] = useState<string>(initialParams.province);

  // Debounce search query 500ms
  const debouncedSearch = useDebounce(searchQuery, 500);

  // Checkbox selection
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // Credits Modal state
  const [editingCreditUser, setEditingCreditUser] = useState<UserProfile | null>(null);
  const [newCreditAmount, setNewCreditAmount] = useState<number>(100);
  const [isUpdatingCredit, setIsUpdatingCredit] = useState(false);

  // VIP Grant Modal state
  const [grantVipUser, setGrantVipUser] = useState<UserProfile | null>(null);
  const [isBulkVipMode, setIsBulkVipMode] = useState<boolean>(false);
  const [vipTier, setVipTier] = useState<number>(1);
  const [durationDays, setDurationDays] = useState<number>(30);
  const [adminNote, setAdminNote] = useState<string>('');
  const [isGrantingVip, setIsGrantingVip] = useState(false);

  // Custom Dialog
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    type?: 'success' | 'error' | 'warning' | 'confirm';
    title: string;
    message: string;
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  // Track initial render to prevent duplicate resets
  const isMountedRef = useRef<boolean>(false);

  // 2. React Query data fetching (with keepPreviousData pagination caching)
  const {
    data: usersQueryData,
    isLoading: loading,
    isFetching,
    refetch
  } = useAdminUsers({
    page: currentPage,
    limit: pageSize,
    search: debouncedSearch,
    status: filterStatus,
    gradeLevel: selectedGrade,
    province: selectedProvince
  });

  const users: UserProfile[] = usersQueryData?.data || [];
  const totalRecords = usersQueryData?.pagination?.totalCount ?? 0;
  const totalPages = usersQueryData?.pagination?.totalPages ?? 1;

  // URL Sync: Cập nhật ?page=...&limit=...&search=...&status=... lên URL trình duyệt
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (currentPage > 1) url.searchParams.set('page', String(currentPage));
      else url.searchParams.delete('page');

      if (pageSize !== 10) url.searchParams.set('limit', String(pageSize));
      else url.searchParams.delete('limit');

      if (debouncedSearch.trim()) url.searchParams.set('search', debouncedSearch.trim());
      else url.searchParams.delete('search');

      if (filterStatus !== 'all') url.searchParams.set('status', filterStatus);
      else url.searchParams.delete('status');

      if (selectedGrade !== 'all') url.searchParams.set('grade', selectedGrade);
      else url.searchParams.delete('grade');

      if (selectedProvince !== 'all') url.searchParams.set('province', selectedProvince);
      else url.searchParams.delete('province');

      window.history.replaceState(null, '', url.toString());
    }
  }, [currentPage, pageSize, debouncedSearch, filterStatus, selectedGrade, selectedProvince]);

  // Reset Page về 1 khi search hoặc filters thay đổi
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    setCurrentPage(1);
  }, [debouncedSearch, filterStatus, selectedGrade, selectedProvince]);

  const fetchUsers = () => {
    queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
  };

  // Danh sách gợi ý Khối lớp & Tỉnh/TP
  const gradeOptions = [
    'Mầm non',
    'Tiểu học',
    'THCS',
    'THPT',
    'Đại học / Cao đẳng',
    'Trung cấp',
    'Cơ quan quản lý',
    'Khác'
  ];

  const provinceOptions = [
    'Hà Nội',
    'TP. Hồ Chí Minh',
    'Đà Nẵng',
    'Hải Phòng',
    'Cần Thơ',
    'Bình Dương',
    'Đồng Nai',
    'Bắc Ninh',
    'Quảng Ninh',
    'Thừa Thiên Huế',
    'Nghệ An',
    'Thanh Hóa',
    'Khác'
  ];

  // Checkbox handlers cho trang hiện tại
  const isAllSelected = users.length > 0 && users.every(u => {
    const uid = u.id || u._id || u.uid || '';
    return selectedUserIds.includes(uid);
  });

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const pageIds = users.map(u => u.id || u._id || u.uid || '').filter(Boolean);
      setSelectedUserIds(prev => Array.from(new Set([...prev, ...pageIds])));
    } else {
      const pageIds = new Set(users.map(u => u.id || u._id || u.uid || ''));
      setSelectedUserIds(prev => prev.filter(id => !pageIds.has(id)));
    }
  };

  const handleSelectRow = (id: string) => {
    setSelectedUserIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // CSV Export with BOM UTF-8
  const exportToCSV = (targetUsers: UserProfile[], filename = 'danh_sach_nguoi_dung_ngs.csv') => {
    if (!targetUsers.length) return;

    const headers = ['ID', 'Họ tên', 'Email', 'Vai trò', 'Khối lớp', 'Tỉnh/TP', 'Đơn vị công tác', 'Số Xu', 'Ngày khởi tạo'];
    const rows = targetUsers.map(u => [
      u.id || u._id || u.uid || '',
      `"${(u.displayName || '').replace(/"/g, '""')}"`,
      `"${(u.email || '').replace(/"/g, '""')}"`,
      u.role === 'admin' ? 'Quản trị viên' : 'Người dùng',
      `"${(u.gradeLevel || '').replace(/"/g, '""')}"`,
      `"${(u.province || '').replace(/"/g, '""')}"`,
      `"${(u.workplace || '').replace(/"/g, '""')}"`,
      u.credits || 0,
      u.createdAt ? new Date(u.createdAt).toLocaleDateString('vi-VN') : ''
    ]);

    // UTF-8 BOM Prefix to properly support Vietnamese diacritics in Excel
    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Bulk Delete
  const handleBulkDelete = () => {
    if (selectedUserIds.length === 0) return;

    setDialog({
      isOpen: true,
      type: 'warning',
      title: 'Xác nhận xóa hàng loạt',
      message: `Bạn có chắc chắn muốn xóa ${selectedUserIds.length} tài khoản người dùng đã chọn? Thao tác này không thể hoàn tác.`,
      onConfirm: async () => {
        setDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
          const res = await customFetch('/api/users/bulk-delete', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ ids: selectedUserIds })
          });

          if (res.ok) {
            const result = await res.json();
            setSelectedUserIds([]);
            fetchUsers();
            setDialog({
              isOpen: true,
              type: 'success',
              title: 'Thành công',
              message: result.message || 'Đã xóa người dùng đã chọn thành công!'
            });
          } else {
            const errData = await res.json();
            setDialog({
              isOpen: true,
              type: 'error',
              title: 'Lỗi',
              message: errData.message || 'Không thể xóa người dùng.'
            });
          }
        } catch (err) {
          setDialog({
            isOpen: true,
            type: 'error',
            title: 'Lỗi kết nối',
            message: 'Đã xảy ra lỗi khi gửi yêu cầu xóa.'
          });
        }
      }
    });
  };

  // Single User Delete
  const handleDeleteSingleUser = (user: UserProfile) => {
    const uid = user.id || user._id || user.uid || '';
    if (!uid) return;

    setDialog({
      isOpen: true,
      type: 'warning',
      title: 'Xác nhận xóa tài khoản',
      message: `Bạn có chắc chắn muốn xóa thành viên "${user.displayName || user.email}"? Thao tác này không thể hoàn tác.`,
      onConfirm: async () => {
        setDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
          const res = await customFetch(`/api/users/${encodeURIComponent(uid)}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (res.ok) {
            setSelectedUserIds(prev => prev.filter(id => id !== uid));
            fetchUsers();
            setDialog({
              isOpen: true,
              type: 'success',
              title: 'Thành công',
              message: 'Đã xóa thành viên thành công!'
            });
          } else {
            const errData = await res.json().catch(() => ({}));
            setDialog({
              isOpen: true,
              type: 'error',
              title: 'Lỗi',
              message: errData.message || 'Không thể xóa người dùng.'
            });
          }
        } catch (err) {
          setDialog({
            isOpen: true,
            type: 'error',
            title: 'Lỗi kết nối',
            message: 'Đã xảy ra lỗi khi gửi yêu cầu xóa thành viên.'
          });
        }
      }
    });
  };

  // Bulk Credits Modal state
  const [showBulkCreditModal, setShowBulkCreditModal] = useState(false);
  const [bulkCreditAmount, setBulkCreditAmount] = useState<number>(100);
  const [bulkCreditAction, setBulkCreditAction] = useState<'add' | 'deduct'>('add');
  const [isUpdatingBulkCredit, setIsUpdatingBulkCredit] = useState(false);

  // Single User Credits Topup/Edit
  const handleOpenCreditModal = (user: UserProfile) => {
    setEditingCreditUser(user);
    setNewCreditAmount(user.credits || 0);
  };

  const handleSaveCredit = async () => {
    if (!editingCreditUser) return;
    setIsUpdatingCredit(true);

    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
      const userId = editingCreditUser.id || editingCreditUser._id || editingCreditUser.uid;
      const res = await customFetch(`/api/users/${userId}/credits`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ credits: newCreditAmount })
      });

      if (res.ok) {
        setEditingCreditUser(null);
        fetchUsers();
        setDialog({
          isOpen: true,
          type: 'success',
          title: 'Cập nhật Ví Xu thành công',
          message: `Đã cập nhật số Xu của ${editingCreditUser.displayName} thành ${newCreditAmount.toLocaleString('vi-VN')} Xu.`
        });
      } else {
        const err = await res.json();
        setDialog({
          isOpen: true,
          type: 'error',
          title: 'Lỗi',
          message: err.message || 'Không thể cập nhật số Xu.'
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'Lỗi',
        message: 'Đã xảy ra lỗi khi kết nối tới máy chủ.'
      });
    } finally {
      setIsUpdatingCredit(false);
    }
  };

  // VIP Grant Handlers
  const handleOpenVipModal = (user: UserProfile) => {
    setIsBulkVipMode(false);
    setGrantVipUser(user);
    // Mặc định chọn vipTier hiện tại của user, nếu user là 0 thì chọn 1 (VIP) hoặc 0 tuỳ ý nhưng hiển thị mặc định theo vipTier hiện tại
    const currentTier = typeof user.vipTier === 'number' ? user.vipTier : (user.vipTier ? Number(user.vipTier) : 0);
    setVipTier(currentTier);
    setDurationDays(currentTier === 0 ? 30 : 30);
    setAdminNote('');
  };

  const handleOpenBulkVipModal = () => {
    if (selectedUserIds.length === 0) return;
    setGrantVipUser(null);
    setIsBulkVipMode(true);
    setVipTier(1); // Mặc định chọn gói VIP
    setDurationDays(30);
    setAdminNote('');
  };

  const handleSaveGrantVip = async () => {
    if (!grantVipUser && !isBulkVipMode) return;
    setIsGrantingVip(true);

    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
      const payload = {
        vipTier: Number(vipTier),
        durationDays: Number(vipTier) === 0 ? 0 : Number(durationDays),
        adminNote: adminNote.trim()
      };

      const tierNames: Record<number, string> = { 0: 'Thành viên thường', 1: 'VIP', 2: 'SVIP' };
      const tierName = tierNames[vipTier] || `Gói VIP ${vipTier}`;

      if (isBulkVipMode) {
        // Bulk grant VIP
        if (selectedUserIds.length === 0) {
          setDialog({
            isOpen: true,
            type: 'warning',
            title: 'Chưa chọn tài khoản',
            message: 'Vui lòng chọn ít nhất một người dùng từ bảng.'
          });
          setIsGrantingVip(false);
          return;
        }

        const res = await customFetch('/api/admins/users/bulk-grant-vip', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            identifiers: selectedUserIds,
            vipTier: payload.vipTier,
            durationDays: payload.durationDays,
            adminNote: payload.adminNote
          })
        });

        const data = await res.json();

        if (res.ok && data.success) {
          setIsBulkVipMode(false);
          const affectedCount = selectedUserIds.length;
          setSelectedUserIds([]);
          await fetchUsers();

          setDialog({
            isOpen: true,
            type: 'success',
            title: 'Cấp VIP Hàng Loạt Thành Công',
            message: data.message || `Đã cập nhật cấp bậc ${tierName} cho ${affectedCount} tài khoản đã chọn.`
          });
        } else {
          setDialog({
            isOpen: true,
            type: 'error',
            title: 'Lỗi cấp VIP hàng loạt',
            message: data.message || 'Không thể cập nhật VIP hàng loạt.'
          });
        }
      } else if (grantVipUser) {
        // Single user grant VIP
        const userId = grantVipUser.id || grantVipUser._id || grantVipUser.uid || '';
        if (!userId) {
          setDialog({
            isOpen: true,
            type: 'error',
            title: 'Lỗi',
            message: 'Không tìm thấy mã định danh người dùng.'
          });
          setIsGrantingVip(false);
          return;
        }

        const res = await customFetch(`/api/admins/users/${encodeURIComponent(userId)}/grant-vip`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });

        const data = await res.json();

        if (res.ok && data.success) {
          setGrantVipUser(null);
          await fetchUsers();

          setDialog({
            isOpen: true,
            type: 'success',
            title: 'Cập nhật VIP thành công',
            message: data.message || `Đã cập nhật cấp bậc ${tierName} cho ${grantVipUser.displayName || grantVipUser.email}.`
          });
        } else {
          setDialog({
            isOpen: true,
            type: 'error',
            title: 'Lỗi cập nhật VIP',
            message: data.message || 'Không thể cập nhật trạng thái VIP cho người dùng.'
          });
        }
      }
    } catch (err) {
      console.error('Lỗi khi cấp VIP thủ công:', err);
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'Lỗi kết nối',
        message: 'Đã xảy ra lỗi khi gửi yêu cầu cập nhật VIP tới máy chủ.'
      });
    } finally {
      setIsGrantingVip(false);
    }
  };

  // Bulk Credits Adjustment Handler
  const handleSaveBulkCredits = async () => {
    if (selectedUserIds.length === 0) return;
    setIsUpdatingBulkCredit(true);

    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
      const res = await customFetch('/api/users/bulk-credits', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ids: selectedUserIds,
          amount: bulkCreditAmount,
          action: bulkCreditAction
        })
      });

      if (res.ok) {
        const data = await res.json();
        setShowBulkCreditModal(false);
        fetchUsers();
        setDialog({
          isOpen: true,
          type: 'success',
          title: 'Cập nhật hàng loạt thành công',
          message: data.message || `Đã ${bulkCreditAction === 'add' ? 'cộng' : 'trừ'} ${bulkCreditAmount} Xu cho ${selectedUserIds.length} người dùng.`
        });
      } else {
        const err = await res.json();
        setDialog({
          isOpen: true,
          type: 'error',
          title: 'Lỗi',
          message: err.message || 'Không thể cập nhật Xu hàng loạt.'
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'Lỗi kết nối',
        message: 'Đã xảy ra lỗi kết nối máy chủ.'
      });
    } finally {
      setIsUpdatingBulkCredit(false);
    }
  };

  return (
    <div className="space-y-6 relative pb-16">
      {/* Top Header Controls */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs">
        <div>
          <h2 className="text-base font-extrabold text-slate-900 flex items-center space-x-2">
            <span>Danh sách Người dùng Hệ thống</span>
            <span className="text-xs font-bold px-2.5 py-0.5 bg-purple-100 text-purple-700 rounded-full">
              {totalRecords} tài khoản
            </span>
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Quản lý tài khoản, phân quyền, cấu hình thông tin công tác & điều chỉnh Ví Xu
          </p>
        </div>

        <div className="flex items-center space-x-2 w-full md:w-auto justify-end">
          <button
            type="button"
            onClick={() => fetchUsers()}
            className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors shrink-0"
            title="Tải lại danh sách"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <button
            type="button"
            onClick={() => exportToCSV(users, 'ngs_nguoi_dung_trang_hien_tai.csv')}
            className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-all shadow-2xs flex items-center space-x-2 shrink-0 active:scale-98"
          >
            <Download className="w-4 h-4" />
            <span>Xuất file CSV</span>
          </button>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 bg-white p-3 rounded-2xl border border-slate-200 shadow-2xs">
        {/* Search */}
        <div className="sm:col-span-5 relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Tìm theo Tên, Email, Đơn vị công tác..."
            className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 pl-9 pr-3 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
          />
        </div>

        {/* Role / Status Filter */}
        <div className="sm:col-span-2">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-xs text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
          >
            <option value="all">Tất cả Vai trò</option>
            <option value="admin">Quản trị viên (Admin)</option>
            <option value="user">Người dùng thông thường</option>
          </select>
        </div>

        {/* Grade Filter */}
        <div className="sm:col-span-2">
          <select
            value={selectedGrade}
            onChange={e => setSelectedGrade(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-xs text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
          >
            <option value="all">Tất cả Khối lớp</option>
            {gradeOptions.map(g => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>

        {/* Province Filter */}
        <div className="sm:col-span-3">
          <select
            value={selectedProvince}
            onChange={e => setSelectedProvince(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-xs text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
          >
            <option value="all">Tất cả Tỉnh/TP</option>
            {provinceOptions.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Bulk Actions Alert / Toolbar right above table */}
      {selectedUserIds.length > 0 && (
        <div className="bg-gradient-to-r from-purple-900 via-slate-900 to-indigo-950 text-white p-3.5 sm:p-4 rounded-2xl shadow-lg border border-purple-700/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 animate-fade-in">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-xl bg-purple-500 text-white text-xs flex items-center justify-center font-black shadow-xs shrink-0">
              {selectedUserIds.length}
            </div>
            <div>
              <div className="text-xs font-extrabold text-white flex items-center space-x-2">
                <span>Đã chọn {selectedUserIds.length} tài khoản</span>
                <span className="px-2 py-0.5 bg-purple-500/30 text-purple-200 border border-purple-400/30 rounded-full text-[10px] font-bold">
                  Thao tác hàng loạt
                </span>
              </div>
              <p className="text-[11px] text-purple-200/90 mt-0.5">
                Chọn một hành động bên dưới để áp dụng đồng loạt cho các tài khoản đã chọn
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={handleOpenBulkVipModal}
              className="px-3.5 py-2 bg-gradient-to-r from-amber-500 to-purple-600 hover:from-amber-600 hover:to-purple-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center space-x-1.5 cursor-pointer active:scale-98"
            >
              <Crown className="w-3.5 h-3.5 text-amber-200" />
              <span>Cấp VIP Hàng Loạt</span>
            </button>

            <button
              type="button"
              onClick={() => setShowBulkCreditModal(true)}
              className="px-3.5 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl transition-all flex items-center space-x-1.5 cursor-pointer active:scale-98"
            >
              <Coins className="w-3.5 h-3.5" />
              <span>Thêm/Trừ Xu</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const selectedList = users.filter(u => selectedUserIds.includes(u.id || u._id || u.uid || ''));
                exportToCSV(selectedList, 'danh_sach_nguoi_dung_da_chon.csv');
              }}
              className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-all flex items-center space-x-1.5 cursor-pointer"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Xuất CSV</span>
            </button>

            <button
              type="button"
              onClick={handleBulkDelete}
              className="px-3 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl transition-all flex items-center space-x-1.5 cursor-pointer active:scale-98"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span>Xóa đã chọn</span>
            </button>

            <button
              type="button"
              onClick={() => setSelectedUserIds([])}
              className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold rounded-xl transition-colors cursor-pointer"
              title="Bỏ chọn tất cả"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-2xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                <th className="py-3 px-4 w-10 text-center">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 cursor-pointer"
                  />
                </th>
                <th className="py-3 px-4">Người dùng</th>
                <th className="py-3 px-4">Khối / Trường</th>
                <th className="py-3 px-4">Tỉnh / TP</th>
                <th className="py-3 px-4 text-center">Ví Xu</th>
                <th className="py-3 px-4 text-center">Vai trò</th>
                <th className="py-3 px-4 text-right">Thao tác</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 text-xs">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-600" />
                    <span>Đang tải danh sách người dùng...</span>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    Chưa tìm thấy người dùng phù hợp với bộ lọc.
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  const uid = user.id || user._id || user.uid || '';
                  const isChecked = selectedUserIds.includes(uid);

                  return (
                    <tr
                      key={uid}
                      className={`hover:bg-purple-50/30 transition-colors ${isChecked ? 'bg-purple-50/50' : ''}`}
                    >
                      {/* Checkbox */}
                      <td className="py-3.5 px-4 text-center">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleSelectRow(uid)}
                          className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 cursor-pointer"
                        />
                      </td>

                      {/* User Info */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-9 h-9 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden shrink-0">
                            {user.photoURL ? (
                              <img src={user.photoURL} alt={user.displayName} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center font-bold text-slate-400">
                                {user.displayName?.slice(0, 1)?.toUpperCase() || <User className="w-4 h-4" />}
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-bold text-slate-900 leading-snug flex items-center space-x-1.5 flex-wrap">
                              <span>{user.displayName}</span>
                              <VipBadge vipTier={user.vipTier} vipExpireAt={user.vipExpireAt} size="xs" />
                            </div>
                            <div className="text-[11px] text-slate-400">{user.email}</div>
                          </div>
                        </div>
                      </td>

                      {/* Grade & Workplace */}
                      <td className="py-3.5 px-4">
                        <div className="font-semibold text-slate-800">{user.gradeLevel || 'Chưa cập nhật'}</div>
                        <div className="text-[11px] text-slate-400 truncate max-w-[160px]">
                          {user.workplace || 'Chưa cập nhật trường'}
                        </div>
                      </td>

                      {/* Province */}
                      <td className="py-3.5 px-4 text-slate-700 font-medium">
                        {user.province || 'Chưa chọn'}
                      </td>

                      {/* Credits (Xu) */}
                      <td className="py-3.5 px-4 text-center">
                        <div className="inline-flex items-center space-x-1.5 px-2.5 py-1 bg-amber-50 text-amber-900 border border-amber-200/80 rounded-xl font-extrabold text-xs">
                          <Coins className="w-3.5 h-3.5 text-amber-600" />
                          <span>{(user.credits || 0).toLocaleString('vi-VN')}</span>
                        </div>
                      </td>

                      {/* Role Badge */}
                      <td className="py-3.5 px-4 text-center">
                        {user.role === 'admin' ? (
                          <span className="px-2.5 py-1 bg-purple-100 text-purple-800 font-extrabold text-[11px] rounded-lg border border-purple-200 inline-flex items-center space-x-1">
                            <Shield className="w-3 h-3 text-purple-600" />
                            <span>Admin</span>
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 bg-slate-100 text-slate-600 font-bold text-[11px] rounded-lg border border-slate-200">
                            Thành viên
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end space-x-1.5">
                          <button
                            type="button"
                            onClick={() => handleOpenVipModal(user)}
                            className="px-2.5 py-1.5 bg-gradient-to-r from-purple-50 to-amber-50 hover:from-purple-100 hover:to-amber-100 text-purple-900 text-[11px] font-bold rounded-lg border border-purple-200/80 transition-all inline-flex items-center space-x-1 shrink-0 shadow-2xs hover:shadow-xs cursor-pointer"
                            title="Cài đặt VIP Thủ Công"
                          >
                            <Crown className="w-3.5 h-3.5 text-amber-600" />
                            <span>Cấp VIP</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleOpenCreditModal(user)}
                            className="px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 text-[11px] font-bold rounded-lg border border-amber-200 transition-colors inline-flex items-center space-x-1 shrink-0 cursor-pointer"
                            title="Nạp/Cộng Xu"
                          >
                            <Coins className="w-3 h-3 text-amber-600" />
                            <span>Cộng Xu</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDeleteSingleUser(user)}
                            className="px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 text-[11px] font-bold rounded-lg border border-rose-200 transition-colors inline-flex items-center space-x-1 shrink-0 cursor-pointer"
                            title="Xóa thành viên"
                          >
                            <Trash2 className="w-3 h-3 text-rose-600" />
                            <span>Xóa</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Server-Side Pagination Bar */}
        {!loading && totalRecords > 0 && (
          <ServerPagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }}
            itemLabel="người dùng"
          />
        )}
      </div>

      {/* Floating Bulk Action Toolbar */}
      {selectedUserIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center space-x-4 border border-slate-800 animate-slide-up">
          <div className="text-xs font-bold text-slate-200 flex items-center space-x-2">
            <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-[11px] flex items-center justify-center font-black">
              {selectedUserIds.length}
            </span>
            <span>Đã chọn {selectedUserIds.length} người dùng</span>
          </div>

          <div className="h-4 w-px bg-slate-700" />

          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={handleOpenBulkVipModal}
              className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-purple-600 hover:from-amber-600 hover:to-purple-700 text-white text-xs font-bold rounded-xl transition-all flex items-center space-x-1.5 cursor-pointer shadow-xs"
            >
              <Crown className="w-3.5 h-3.5 text-amber-200" />
              <span>Cấp VIP Hàng Loạt</span>
            </button>

            <button
              type="button"
              onClick={() => setShowBulkCreditModal(true)}
              className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl transition-all flex items-center space-x-1.5 cursor-pointer"
            >
              <Coins className="w-3.5 h-3.5" />
              <span>Thêm/Trừ Xu</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const selectedList = users.filter(u => selectedUserIds.includes(u.id || u._id || u.uid || ''));
                exportToCSV(selectedList, 'danh_sach_nguoi_dung_da_chon.csv');
              }}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-all flex items-center space-x-1.5 cursor-pointer"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Xuất CSV</span>
            </button>

            <button
              type="button"
              onClick={handleBulkDelete}
              className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl transition-all flex items-center space-x-1.5 cursor-pointer"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span>Xóa hàng loạt</span>
            </button>
          </div>

          <button
            type="button"
            onClick={() => setSelectedUserIds([])}
            className="p-1 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors ml-2"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Credit Edit / Top-up Modal */}
      {editingCreditUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-slate-100 relative space-y-4">
            <button
              type="button"
              onClick={() => setEditingCreditUser(null)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                <Coins className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-extrabold text-slate-900">
                  Điều chỉnh Ví Xu
                </h3>
                <p className="text-xs text-slate-500">
                  {editingCreditUser.displayName} ({editingCreditUser.email})
                </p>
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Số Xu khả dụng mới:
                </label>
                <input
                  type="number"
                  value={newCreditAmount}
                  onChange={e => setNewCreditAmount(Number(e.target.value))}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl p-2.5 text-sm font-extrabold text-amber-900 focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>

              {/* Quick Preset Buttons */}
              <div className="flex items-center gap-1.5">
                {[100, 500, 1000, 2000].map(val => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setNewCreditAmount(prev => prev + val)}
                    className="flex-1 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-900 font-bold text-[11px] rounded-lg border border-amber-200 transition-colors"
                  >
                    +{val}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setEditingCreditUser(null)}
                className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl"
              >
                Hủy bỏ
              </button>
              <button
                type="button"
                disabled={isUpdatingCredit}
                onClick={handleSaveCredit}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center space-x-1.5"
              >
                {isUpdatingCredit && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                <span>Lưu thay đổi</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Credit Adjustment Modal */}
      {showBulkCreditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-slate-100 relative space-y-4">
            <button
              type="button"
              onClick={() => setShowBulkCreditModal(false)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <Coins className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-extrabold text-slate-900">
                  Cộng/Trừ Xu hàng loạt
                </h3>
                <p className="text-xs text-slate-500">
                  Áp dụng cho {selectedUserIds.length} tài khoản đã chọn
                </p>
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Hành động:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setBulkCreditAction('add')}
                    className={`py-2 text-xs font-extrabold rounded-xl border transition-all ${
                      bulkCreditAction === 'add'
                        ? 'bg-amber-500 text-white border-amber-600 shadow-xs'
                        : 'bg-slate-50 text-slate-600 border-slate-200'
                    }`}
                  >
                    + Cộng Xu
                  </button>
                  <button
                    type="button"
                    onClick={() => setBulkCreditAction('deduct')}
                    className={`py-2 text-xs font-extrabold rounded-xl border transition-all ${
                      bulkCreditAction === 'deduct'
                        ? 'bg-rose-600 text-white border-rose-700 shadow-xs'
                        : 'bg-slate-50 text-slate-600 border-slate-200'
                    }`}
                  >
                    - Trừ Xu
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Số lượng Xu:
                </label>
                <input
                  type="number"
                  value={bulkCreditAmount}
                  onChange={e => setBulkCreditAmount(Number(e.target.value))}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl p-2.5 text-sm font-extrabold text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>

              <div className="flex items-center gap-1.5">
                {[50, 100, 500, 1000].map(val => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setBulkCreditAmount(val)}
                    className="flex-1 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-[11px] rounded-lg transition-colors"
                  >
                    {val}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowBulkCreditModal(false)}
                className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl cursor-pointer"
              >
                Hủy bỏ
              </button>
              <button
                type="button"
                disabled={isUpdatingBulkCredit}
                onClick={handleSaveBulkCredits}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center space-x-1.5 disabled:opacity-50 cursor-pointer"
              >
                {isUpdatingBulkCredit && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                <span>Xác nhận thực hiện</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== MODAL CÀI ĐẶT VIP THỦ CÔNG ==================== */}
      {(grantVipUser || isBulkVipMode) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 relative space-y-4">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => {
                setGrantVipUser(null);
                setIsBulkVipMode(false);
              }}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Modal Header */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-amber-500 to-purple-600 text-white flex items-center justify-center shrink-0 shadow-md">
                <Crown className="w-5 h-5 text-amber-100" />
              </div>
              <div>
                <h3 className="text-sm font-extrabold text-slate-900 flex items-center space-x-1.5">
                  <span>{isBulkVipMode ? 'Cài đặt VIP Hàng Loạt' : 'Cài đặt VIP Thủ Công'}</span>
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                </h3>
                <p className="text-xs text-slate-500">
                  {isBulkVipMode
                    ? `Áp dụng cấp bậc và thời hạn VIP cho ${selectedUserIds.length} tài khoản đã chọn`
                    : 'Cấp, gia hạn hoặc hạ cấp bậc VIP cho người dùng'}
                </p>
              </div>
            </div>

            {/* User Target Card Info */}
            {isBulkVipMode ? (
              <div className="p-3.5 bg-gradient-to-r from-purple-50 to-amber-50 rounded-2xl border border-purple-200/80 flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center font-extrabold text-xs shrink-0 shadow-xs">
                  <Users className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-extrabold text-purple-950 flex items-center space-x-1.5">
                    <span>Đang thao tác trên {selectedUserIds.length} tài khoản</span>
                    <span className="px-1.5 py-0.5 bg-purple-200 text-purple-800 text-[10px] rounded-md font-extrabold">
                      Hàng loạt
                    </span>
                  </div>
                  <div className="text-[11px] text-purple-700/90 truncate mt-0.5">
                    Tất cả {selectedUserIds.length} tài khoản được chọn sẽ được cập nhật đồng bộ.
                  </div>
                </div>
              </div>
            ) : grantVipUser ? (
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200/80 flex items-center justify-between">
                <div className="flex items-center space-x-2.5 overflow-hidden">
                  <div className="w-8 h-8 rounded-xl bg-purple-100 border border-purple-200 text-purple-700 flex items-center justify-center font-bold text-xs shrink-0">
                    {grantVipUser.photoURL ? (
                      <img src={grantVipUser.photoURL} alt={grantVipUser.displayName} className="w-full h-full object-cover rounded-xl" />
                    ) : (
                      grantVipUser.displayName?.slice(0, 1)?.toUpperCase() || 'U'
                    )}
                  </div>
                  <div className="truncate">
                    <div className="text-xs font-bold text-slate-900 truncate">
                      {grantVipUser.displayName || 'Người dùng'}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {grantVipUser.email}
                    </div>
                  </div>
                </div>

                <div className="text-right shrink-0 ml-2">
                  <div className="text-[10px] text-slate-400 font-medium">Hiện tại</div>
                  <div className="flex items-center space-x-1 justify-end mt-0.5">
                    {grantVipUser.vipTier && grantVipUser.vipTier > 0 ? (
                      <VipBadge vipTier={grantVipUser.vipTier} vipExpireAt={grantVipUser.vipExpireAt} size="xs" />
                    ) : (
                      <span className="text-[11px] font-bold text-slate-600 px-2 py-0.5 bg-slate-200 rounded-md">
                        Thành viên thường
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : null}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveGrantVip();
              }}
              className="space-y-3.5 pt-1"
            >
              {/* TRƯỜNG 1: Chọn Cấp độ VIP */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  1. Cấp bậc VIP: <span className="text-rose-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {/* Option 0: Thường */}
                  <button
                    type="button"
                    onClick={() => {
                      setVipTier(0);
                      setDurationDays(0);
                    }}
                    className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between ${
                      vipTier === 0
                        ? 'border-slate-800 bg-slate-900 text-white shadow-xs'
                        : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <User className={`w-3.5 h-3.5 ${vipTier === 0 ? 'text-slate-300' : 'text-slate-500'}`} />
                      {vipTier === 0 && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="font-extrabold text-xs">User Thường</div>
                    <div className={`text-[10px] ${vipTier === 0 ? 'text-slate-400' : 'text-slate-500'}`}>
                      Cấp 0 (Mặc định)
                    </div>
                  </button>

                  {/* Option 1: VIP */}
                  <button
                    type="button"
                    onClick={() => {
                      setVipTier(1);
                      if (durationDays === 0) setDurationDays(30);
                    }}
                    className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between ${
                      vipTier === 1
                        ? 'border-amber-500 bg-amber-500 text-white shadow-xs'
                        : 'border-amber-200/80 bg-amber-50/50 hover:bg-amber-50 text-amber-900'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Crown className={`w-3.5 h-3.5 ${vipTier === 1 ? 'text-amber-100' : 'text-amber-600'}`} />
                      {vipTier === 1 && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="font-extrabold text-xs">Gói VIP</div>
                    <div className={`text-[10px] ${vipTier === 1 ? 'text-amber-100' : 'text-amber-700'}`}>
                      Cấp 1 (Ưu đãi VIP)
                    </div>
                  </button>

                  {/* Option 2: SVIP */}
                  <button
                    type="button"
                    onClick={() => {
                      setVipTier(2);
                      if (durationDays === 0) setDurationDays(30);
                    }}
                    className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between ${
                      vipTier === 2
                        ? 'border-purple-600 bg-gradient-to-r from-purple-700 to-indigo-700 text-white shadow-xs'
                        : 'border-purple-200/80 bg-purple-50/50 hover:bg-purple-50 text-purple-900'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Gem className={`w-3.5 h-3.5 ${vipTier === 2 ? 'text-purple-200' : 'text-purple-600'}`} />
                      {vipTier === 2 && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="font-extrabold text-xs">Gói SVIP</div>
                    <div className={`text-[10px] ${vipTier === 2 ? 'text-purple-200' : 'text-purple-700'}`}>
                      Cấp 2 (Đặc quyền)
                    </div>
                  </button>
                </div>
              </div>

              {/* TRƯỜNG 2: Số ngày muốn cộng thêm (durationDays) */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-slate-700">
                    2. Số ngày cộng thêm (durationDays):
                  </label>
                  {vipTier > 0 && (
                    <span className="text-[10px] text-purple-700 font-bold">
                      {durationDays === 0 ? 'Vô thời hạn (Vĩnh viễn)' : `+${durationDays} ngày`}
                    </span>
                  )}
                </div>

                {vipTier === 0 ? (
                  <div className="p-2.5 bg-slate-100 rounded-xl border border-slate-200 text-xs text-slate-500 font-medium">
                    Tài khoản Thành viên thường không áp dụng thời hạn VIP (Tự động đặt 0 ngày).
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <input
                        type="number"
                        min={0}
                        max={3650}
                        required
                        value={durationDays}
                        onChange={(e) => setDurationDays(Math.max(0, parseInt(e.target.value) || 0))}
                        placeholder="VD: 30"
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl pl-3.5 pr-14 py-2 text-xs font-mono font-extrabold text-slate-900 focus:bg-white focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-bold">
                        ngày
                      </span>
                    </div>

                    {/* Presets Button */}
                    <div className="grid grid-cols-5 gap-1">
                      {[
                        { label: '+30 ngày', val: 30 },
                        { label: '+90 ngày', val: 90 },
                        { label: '+180 ngày', val: 180 },
                        { label: '+1 năm', val: 365 },
                        { label: 'Vĩnh viễn', val: 0 }
                      ].map((item) => (
                        <button
                          key={item.val}
                          type="button"
                          onClick={() => setDurationDays(item.val)}
                          className={`py-1 text-[10px] font-bold rounded-lg border transition-colors cursor-pointer ${
                            durationDays === item.val
                              ? 'bg-purple-100 text-purple-800 border-purple-300'
                              : 'bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200'
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-400">
                      💡 Điền 0 nếu muốn cấp VIP Vô thời hạn (Vĩnh viễn). Nếu user đã có hạn VIP, số ngày sẽ được cộng tiếp nối.
                    </p>
                  </div>
                )}
              </div>

              {/* TRƯỜNG 3: Ghi chú của Admin (adminNote) */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  3. Ghi chú của Admin (adminNote):
                </label>
                <textarea
                  rows={2}
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="Lý do cấp tay (VD: Tặng giáo viên tiêu biểu, Thưởng sự kiện cộng đồng, Hỗ trợ kỹ thuật...)"
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none resize-none"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setGrantVipUser(null)}
                  className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl transition-colors cursor-pointer"
                >
                  Hủy bỏ
                </button>

                <button
                  type="submit"
                  disabled={isGrantingVip}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-amber-600 hover:from-purple-700 hover:to-amber-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center space-x-1.5 disabled:opacity-50 cursor-pointer"
                >
                  {isGrantingVip ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Đang lưu...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>Lưu / Xác nhận Cấp VIP</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Dialog */}
      <CustomDialog
        isOpen={dialog.isOpen}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        onClose={() => setDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={dialog.onConfirm}
      />
    </div>
  );
};

export default UserManagement;
